import { ref } from 'vue'

/**
 * Where the bearer token lives.
 *
 * The API issues a 12-hour sliding Sanctum token and has no refresh endpoint:
 * the console re-validates it against `/auth/me` on load. `sessionStorage`
 * would therefore drop the session on every tab close and defeat the twelve
 * hours, so `localStorage` it is. The cost is that any script running on this
 * origin can read the token, which is the usual trade. The defence is the
 * `script-src 'self'` policy emitted into `_headers` at build time, plus never
 * rendering untrusted HTML.
 *
 * This module holds no other state, which is what keeps the api client and the
 * auth store from importing each other.
 */

const STORAGE_KEY = 'console.auth.token'
const STAGE_KEY = 'console.auth.stage'

/** How far a token has got through multi-factor authentication. */
export type TokenStage = 'setup_required' | 'verify_required' | 'verified'

function read(key: string): string | null {
  try {
    return window.localStorage.getItem(key)
  } catch {
    // Private windows and blocked site data throw rather than return null.
    return null
  }
}

function write(key: string, value: string | null): void {
  try {
    if (value === null) {
      window.localStorage.removeItem(key)
    } else {
      window.localStorage.setItem(key, value)
    }
  } catch {
    // Losing persistence costs the user a sign-in on the next load. Losing the
    // whole session because storage was unavailable would cost them this one.
  }
}

function readStage(): TokenStage | null {
  const value = read(STAGE_KEY)
  return value === 'setup_required' || value === 'verify_required' || value === 'verified'
    ? value
    : null
}

export const token = ref<string | null>(read(STORAGE_KEY))

/**
 * Kept beside the token because `/auth/me` refuses a pre-MFA token, so on a
 * reload mid-flow it is the only way to know which MFA page to return to.
 * It is a hint, never an authority: the API still gates every call.
 */
export const tokenStage = ref<TokenStage | null>(token.value === null ? null : readStage())

export function setToken(value: string, stage: TokenStage): void {
  token.value = value
  tokenStage.value = stage
  write(STORAGE_KEY, value)
  write(STAGE_KEY, stage)
}

export function clearToken(): void {
  token.value = null
  tokenStage.value = null
  tenantReadOnly.value = false
  write(STORAGE_KEY, null)
  write(STAGE_KEY, null)
}

/**
 * True while the tenant's subscription is `read_only` (MRS 6.10): every write
 * outside auth, billing and notification preferences is refused with a 402.
 * Set from the subscription when the session can read it, and by the API
 * client the moment any call earns that 402, so a helpdesk user who cannot
 * read the subscription still learns why their write was refused. Lives here
 * beside the token so the permission check and the client share it without
 * either importing a store.
 */
export const tenantReadOnly = ref(false)

export function blockTenantWrites(): void {
  tenantReadOnly.value = true
}

/**
 * Set when the API rejected a token this browser was still using, so the
 * sign-in page can say "your session ended" instead of nothing. It is a flag
 * rather than a redirect because this module must not know about the router:
 * the shell watches `token` and does the navigating.
 */
export const sessionExpired = ref(false)

export function expireSession(): void {
  if (token.value === null) return
  sessionExpired.value = true
  clearToken()
}

export function acknowledgeExpiry(): void {
  sessionExpired.value = false
}
